import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { ApiError, completePasswordReset, requestPasswordReset } from './api';
import { BrandLockup } from './BrandLockup';
import { SupportFooter } from './SupportFooter';

export function PasswordResetPage({ requestOnly = false }: { requestOnly?: boolean }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const token = new URLSearchParams(location.hash.replace(/^#/, '')).get('token') ?? '';
  const requestMutation = useMutation({ mutationFn: () => requestPasswordReset(email) });
  const completeMutation = useMutation({
    mutationFn: () => completePasswordReset(token, password),
    onSuccess: () => void navigate('/login', { replace: true }),
  });
  const error = requestMutation.error ?? completeMutation.error;

  return (
    <Container component="main" maxWidth="sm" sx={{ py: { xs: 5, md: 10 } }}>
      <Stack spacing={4}>
        <Box>
          <Box
            sx={{
              bgcolor: 'primary.dark',
              borderRadius: 2.5,
              display: 'inline-flex',
              px: 2,
              py: 1.5,
            }}
          >
            <BrandLockup />
          </Box>
          <Typography component="h1" sx={{ mt: 1 }} variant="h2">
            {requestOnly ? 'Recupera l’accesso.' : 'Scegli una nuova password.'}
          </Typography>
        </Box>
        <Card>
          <CardContent sx={{ p: { xs: 3, md: 4 } }}>
            {requestOnly ? (
              <Stack spacing={3}>
                <TextField
                  autoComplete="email"
                  autoFocus
                  fullWidth
                  label="Email"
                  onChange={(event) => setEmail(event.target.value)}
                  type="email"
                  value={email}
                />
                <Button
                  disabled={!email || requestMutation.isPending || requestMutation.isSuccess}
                  onClick={() => requestMutation.mutate()}
                  variant="contained"
                >
                  Invia istruzioni
                </Button>
                {requestMutation.isSuccess && (
                  <Alert severity="success">
                    Se l’account è attivo, riceverai un messaggio con le istruzioni.
                  </Alert>
                )}
              </Stack>
            ) : (
              <Stack spacing={3}>
                {!token && <Alert severity="warning">Link di recupero non valido.</Alert>}
                <TextField
                  autoComplete="new-password"
                  fullWidth
                  helperText="Almeno 12 caratteri; usa una password unica."
                  label="Nuova password"
                  onChange={(event) => setPassword(event.target.value)}
                  type="password"
                  value={password}
                />
                <TextField
                  autoComplete="new-password"
                  fullWidth
                  label="Conferma password"
                  onChange={(event) => setConfirmation(event.target.value)}
                  type="password"
                  value={confirmation}
                />
                <Button
                  disabled={
                    !token ||
                    password.length < 12 ||
                    password !== confirmation ||
                    completeMutation.isPending
                  }
                  onClick={() => completeMutation.mutate()}
                  variant="contained"
                >
                  Reimposta password
                </Button>
              </Stack>
            )}
            {error && (
              <Alert severity="error" sx={{ mt: 3 }}>
                {error instanceof ApiError ? error.message : 'Operazione non riuscita'}
              </Alert>
            )}
          </CardContent>
        </Card>
        <SupportFooter />
      </Stack>
    </Container>
  );
}
